/**
 * Reporting bucket for a menu category , the axis behind the food/drinks split in
 * POS analytics and the operations reports.
 *
 * `menu_categories.category_type` is a Postgres enum: 'food' | 'drink' | 'other',
 * defaulting to 'food'. Nothing in the dashboard ever set it, so every category a
 * merchant created , including one literally named "Drinks" , was stored as food, and
 * the drinks line of every revenue report read zero. For a bar or a café that is the
 * number the business is run on.
 *
 * Migration 20260521010000 already backfilled existing rows by matching the category
 * NAME against this vocabulary. This applies the same rule at creation time so new
 * categories are classified the same way, and exports it so the UI can show and
 * override the guess rather than silently inheriting a wrong default.
 */
export type MenuCategoryType = 'food' | 'drink' | 'other';

const DRINK_PATTERN =
  /(drink|drinks|beverage|beverages|coffee|tea|latte|espresso|cappuccino|juice|smoothie|soda|cocktail|mocktail|beer|wine|spirit|spirits|liquor|whisky|whiskey|vodka|rum|gin)/;

const OTHER_PATTERN = /(merch|gift|retail|service|fees|other|extras)/;

/** Best guess at a category's reporting bucket from its name. Mirrors the migration. */
export function inferMenuCategoryType(name: string): MenuCategoryType {
  const normalized = (name || '').toLowerCase();
  if (DRINK_PATTERN.test(normalized)) return 'drink';
  if (OTHER_PATTERN.test(normalized)) return 'other';
  return 'food';
}

export const MENU_CATEGORY_TYPE_OPTIONS: Array<{ value: MenuCategoryType; label: string }> = [
  { value: 'food', label: 'Food' },
  { value: 'drink', label: 'Drinks' },
  { value: 'other', label: 'Other' },
];

export function menuCategoryTypeLabel(value: string | null | undefined): string {
  return MENU_CATEGORY_TYPE_OPTIONS.find((option) => option.value === value)?.label || 'Food';
}
